import React, { useState, useEffect } from "react";
import {
    Trophy, Medal, Award, Calendar, User, Globe, BookOpen, TrendingUp,
    Filter, Users, Star, Shield,
    ArrowUp, ArrowDown, Minus, Crown, Sparkles, ChevronRight
} from "lucide-react";
import { API_BASE_URL } from "../../config/apiConfig";
import { useNavigate } from "react-router-dom";
import { FaArrowLeft } from "react-icons/fa";

const subjectIcons = {
    "Physics": "⚛️",
    "Chemistry": "🧪",
    "Biology": "🍃",
    "Further Mathematics": "📐",
    "Mathematics": "🔢",
    "Agricultural Science": "🌱",
    "Commerce": "📈",
    "CRS": "✝️",
    "Christian Religious Studies (CRS)": "✝️",
    "English Language": "✍️",
    "English Studies": "📚",
    "Basic Science": "🔬",
    "Basic Technology": "💻",
    "Social Studies": "🌍",
    "Civic Education": "👥",
    "Computer Studies": "💻",
    "Physical and Health Education": "❤️",
    "Physical and Health Education (PHE)": "❤️",
    "Business Studies": "💼",
    "Creative Arts": "🎨",
    "Home Economics": "🏠",
    "Security Education": "🛡️",
    "Literature": "📖",
    "Geography": "🌍",
    "History": "📜",
    "Economics": "📊",
    "Government": "🏛️",
    "French": "🇫🇷",
    "Arabic": "📚",
    "Hausa": "📚",
    "Igbo": "📚",
    "Yoruba": "📚",
    "Fine Arts": "🎨",
    "Music": "🎵",
    "Technical Drawing": "📏",
    "Auto Mechanics": "🔧",
    "Electronics": "⚡",
    "Building Construction": "🏗️",
    "Metal Work": "⚒️",
    "Wood Work": "🌲",
    "Food and Nutrition": "🍎",
    "Clothing and Textiles": "👕"
};

const groupIcons = {
    "admin": "👑",
    "moderator": "🛡️",
    "member": "👤"
};

export default function LeaderboardIndividual() {
    const [userData, setUserData] = useState(null);
    const [selectedTimePeriod, setSelectedTimePeriod] = useState('7days');
    const [selectedSubject, setSelectedSubject] = useState('global');
    const [selectedGroup, setSelectedGroup] = useState('global');
    const [leaderboardData, setLeaderboardData] = useState([]);
    const [userPosition, setUserPosition] = useState(null);
    const [loading, setLoading] = useState(false);
    const [userSubjects, setUserSubjects] = useState([]);
    const [userGroups, setUserGroups] = useState([]);
    const [animateCards, setAnimateCards] = useState(false);
    const [examData, setExamData] = useState([]);
    const user_id = localStorage.getItem("child_id")

    const timePeriods = [
        { value: '7days', label: '7 Days', icon: Calendar, gradient: 'from-blue-400 to-blue-600' },
        { value: '30days', label: '30 Days', icon: Calendar, gradient: 'from-indigo-400 to-indigo-600' },
        { value: '1year', label: '1 Year', icon: Calendar, gradient: 'from-purple-400 to-purple-600' },
        { value: 'alltime', label: 'All Time', icon: TrendingUp, gradient: 'from-blue-600 to-indigo-800' }
    ];

    const navigate = useNavigate();

    const goBack = () => {
        navigate(-1);
    };

    // Utility to filter by time period
    const getTimeFilter = (timePeriod) => {
        const now = new Date();
        switch (timePeriod) {
            case "7days":
                return new Date(now.setDate(now.getDate() - 7));
            case "30days":
                return new Date(now.setDate(now.getDate() - 30));
            case "1year":
                return new Date(now.setFullYear(now.getFullYear() - 1));
            case "alltime":
            default:
                return null;
        }
    };

    const calculateLeaderboard = (examData, userData, timePeriod = "7days", subject = "global", group = "global") => {
        if (!examData || examData.length === 0) return { leaderboard: [], userPosition: null };

        const timeFilter = getTimeFilter(timePeriod);

        // Filter by time, subject, and group
        let filteredData = examData;

        if (timeFilter) {
            filteredData = filteredData.filter(exam => new Date(exam.createdAt) >= timeFilter);
        }

        if (subject !== "global") {
            filteredData = filteredData.filter(exam => exam.subject === subject);
        }

        if (group !== "global") {
            filteredData = filteredData.filter(exam => {
                return exam.user?.groups?.some(userGroup => userGroup.group_id.toString() === group);
            });
        }

        // Aggregate stats per user
        const userStats = {};
        filteredData.forEach(exam => {
            const userId = exam.user_id;
            if (!userStats[userId]) {
                userStats[userId] = {
                    user_id: userId,
                    totalQuestions: 0,
                    correctAnswers: 0,
                    examCount: new Set(),
                    firstName: exam.user?.firstName || "Unknown",
                    lastName: exam.user?.lastName || "",
                    groups: exam.user?.groups || [],
                };
            }
            userStats[userId].totalQuestions += 1;
            userStats[userId].correctAnswers += exam.is_correct ? 1 : 0;
            userStats[userId].examCount.add(exam.test_id);
        });

        // Convert stats to leaderboard array
        const leaderboardArray = Object.values(userStats).map(stat => {
            const accuracy = stat.totalQuestions > 0 ? (stat.correctAnswers / stat.totalQuestions) * 100 : 0;
            const examCount = stat.examCount.size;
            const score = Math.round((accuracy * stat.totalQuestions) + (examCount * 10));

            return {
                user_id: stat.user_id,
                name: `${stat.firstName} ${stat.lastName}`.trim(),
                score,
                examsCount: examCount,
                averageScore: Math.round(accuracy),
                totalQuestions: stat.totalQuestions,
                correctAnswers: stat.correctAnswers,
                subject: subject !== "global" ? subject : null,
                groups: stat.groups,
                change: 0, // Needs historical data if you want it
            };
        });

        // Sort descending by score
        leaderboardArray.sort((a, b) => b.score - a.score);

        // Assign ranks
        leaderboardArray.forEach((user, index) => {
            user.rank = index + 1;
        });

        const userPosition = leaderboardArray.find(u => u.user_id === userData?.user_id) || null;

        return { leaderboard: leaderboardArray, userPosition };
    };

    // Fetch user profile
    useEffect(() => {
        const fetchProfile = async () => {
            try {
                const response = await fetch(`${API_BASE_URL}/api/parents/${user_id}`, {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                        "Authorization": `Bearer ${localStorage.getItem("token")}`
                    },
                });

                if (!response.ok) {
                    const errorData = await response.json();
                    throw new Error(errorData.message || "Failed to fetch user profile");
                }

                const user = await response.json();
                setUserData(user);

                // Parse user subjects
                try {
                    const subjects = JSON.parse(user.subjects || "[]");
                    setUserSubjects(Array.isArray(subjects) ? subjects : []);
                } catch (e) {
                    console.error("Error parsing subjects:", e);
                    setUserSubjects([]);
                }

            } catch (error) {
                console.error("Error fetching user profile:", error.message);
            }
        };

        fetchProfile();
    }, []);

    // Fetch exam data for leaderboard calculation
    useEffect(() => {
        const fetchExamData = async () => {
            try {
                const response = await fetch(`${API_BASE_URL}/api/tests/leader-board`, {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                        "Authorization": `Bearer ${localStorage.getItem("token")}`
                    },
                });

                if (!response.ok) {
                    const errorData = await response.json();
                    throw new Error(errorData.message || "Failed to fetch exam data");
                }

                const result = await response.json();
                setExamData(result.data || []);

                // Extract unique groups from exam data
                const groupsSet = new Set();
                result.data?.forEach(exam => {
                    exam.user?.groups?.forEach(group => {
                        groupsSet.add(JSON.stringify({
                            id: group.group_id,
                            name: group.groupName,
                            description: group.description
                        }));
                    });
                });

                const uniqueGroups = Array.from(groupsSet).map(groupStr => JSON.parse(groupStr));
                setUserGroups(uniqueGroups);

            } catch (error) {
                console.error("Error fetching exam data:", error.message);
                setExamData([]);
            }
        };

        fetchExamData();
    }, []);

    // Calculate leaderboard when data or filters change
    useEffect(() => {
        if (examData.length > 0 && userData) {
            setLoading(true);
            setAnimateCards(false);

            // Simulate loading delay for better UX
            setTimeout(() => {
                const result = calculateLeaderboard(examData, userData, selectedTimePeriod, selectedSubject, selectedGroup);
                setLeaderboardData(result.leaderboard);
                setUserPosition(result.userPosition);

                setLoading(false);
                setTimeout(() => setAnimateCards(true), 100);
            }, 500);
        }
    }, [examData, userData, selectedTimePeriod, selectedSubject, selectedGroup]);

    useEffect(() => {
        window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
    }, []);

    const getRankIcon = (rank) => {
        if (rank === 1) return <Crown className="w-6 h-6 text-yellow-500 animate-pulse" />;
        if (rank === 2) return <Medal className="w-6 h-6 text-gray-400" />;
        if (rank === 3) return <Medal className="w-6 h-6 text-amber-600" />;
        return <Award className="w-5 h-5 text-blue-500" />;
    };

    const getRankBadgeColor = (rank) => {
        if (rank === 1) return "bg-gradient-to-r from-yellow-400 to-yellow-600 text-white shadow-lg";
        if (rank === 2) return "bg-gradient-to-r from-gray-300 to-gray-500 text-white shadow-md";
        if (rank === 3) return "bg-gradient-to-r from-amber-400 to-amber-600 text-white shadow-md";
        return "bg-gradient-to-r from-blue-400 to-blue-600 text-white shadow-sm";
    };

    const getChangeIcon = (change) => {
        if (change > 0) return <ArrowUp className="w-4 h-4 text-green-500" />;
        if (change < 0) return <ArrowDown className="w-4 h-4 text-red-500" />;
        return <Minus className="w-4 h-4 text-gray-400" />;
    };

    const SubjectIcon = ({ subject, className = "" }) => {
        return <span className={className}>{subjectIcons[subject] || "📚"}</span>;
    };

    const getUserGroupBadge = (groups) => {
        if (!groups || groups.length === 0) return null;

        const currentGroup = selectedGroup !== 'global'
            ? groups.find(g => g.group_id.toString() === selectedGroup)
            : groups[0]; // Show first group if global

        if (!currentGroup) return null;

        const roleIcon = groupIcons[currentGroup.user_groups?.role] || "👤";

        return (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-indigo-100 text-indigo-800 text-xs rounded-full font-medium">
                {roleIcon} {currentGroup.groupName}
            </span>
        );
    };

    const displayName = userData?.firstName || "Your child";

    if (!userData) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 flex items-center justify-center">
                <div className="text-center">
                    <div className="relative w-16 h-16 mx-auto mb-4">
                        <div className="absolute inset-0 border-4 border-blue-200 rounded-full"></div>
                        <div className="absolute inset-0 border-4 border-blue-600 rounded-full border-t-transparent animate-spin"></div>
                    </div>
                    <p className="text-lg text-gray-600 font-medium">Loading user data...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 p-6">
            <div className="flex items-center mb-6">
                <button
                    onClick={goBack}
                    className="flex items-center gap-2 text-[#2148C0] hover:text-blue-600 font-medium transition"
                >
                    <FaArrowLeft className="text-xl" />
                    Back
                </button>
            </div>
            <div className="max-w-7xl mx-auto">
                {/* Animated Header */}
                <div className="mb-8 text-center">
                    <div className="inline-flex items-center gap-3 mb-4">
                        <div className="relative">
                            <Trophy className="text-yellow-500 w-12 h-12 animate-bounce" />
                            <Sparkles className="absolute -top-1 -right-1 w-6 h-6 text-yellow-400 animate-pulse" />
                        </div>
                    </div>
                    <h1 className="text-4xl font-bold bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 bg-clip-text text-transparent mb-3">
                        Leaderboard
                    </h1>
                    <p className="text-gray-600 text-lg">Track {displayName}'s performance and see how they compare</p>
                </div>

                {/* Enhanced Controls with Group Filter */}
                <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl border border-blue-100 p-8 mb-8 transition-all duration-300 hover:shadow-2xl">
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                        {/* Time Period Selection */}
                        <div>
                            <label className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
                                <Calendar className="w-4 h-4" />
                                Time Period
                            </label>
                            <div className="grid grid-cols-2 gap-3">
                                {timePeriods.map((period, index) => (
                                    <button
                                        key={period.value}
                                        onClick={() => setSelectedTimePeriod(period.value)}
                                        style={{ animationDelay: `${index * 100}ms` }}
                                        className={`group p-4 rounded-xl border-2 text-sm font-semibold transition-all duration-300 transform hover:scale-105 animate-fade-in ${selectedTimePeriod === period.value
                                            ? `bg-gradient-to-r ${period.gradient} text-white border-transparent shadow-lg`
                                            : 'bg-white text-gray-700 border-gray-200 hover:border-blue-300 hover:bg-blue-50'
                                            }`}
                                    >
                                        <period.icon className={`w-5 h-5 mx-auto mb-2 ${selectedTimePeriod === period.value ? 'text-white' : 'text-blue-500'
                                            } transition-colors`} />
                                        {period.label}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Subject Selection */}
                        <div>
                            <label className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
                                <Filter className="w-4 h-4" />
                                Subject Filter
                            </label>
                            <select
                                value={selectedSubject}
                                onChange={(e) => setSelectedSubject(e.target.value)}
                                className="w-full p-4 border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-blue-500/20 focus:border-blue-500 bg-white text-gray-700 font-medium transition-all duration-200 hover:border-blue-300"
                            >
                                <option value="global">🌍 Global Ranking</option>
                                {userSubjects.map((subject) => (
                                    <option key={subject} value={subject}>
                                        {subjectIcons[subject] || "📚"} {subject}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* Group Selection */}
                        <div>
                            <label className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
                                <Shield className="w-4 h-4" />
                                Group Filter
                            </label>
                            <select
                                value={selectedGroup}
                                onChange={(e) => setSelectedGroup(e.target.value)}
                                className="w-full p-4 border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-indigo-500/20 focus:border-indigo-500 bg-white text-gray-700 font-medium transition-all duration-200 hover:border-indigo-300"
                            >
                                <option value="global">👥 All Groups</option>
                                {userGroups.map((group) => (
                                    <option key={group.id} value={group.id.toString()}>
                                        🏆 {group.name}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                </div>

                {/* Enhanced User Position Card */}
                {userPosition && (
                    <div className={`bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 rounded-2xl shadow-2xl p-8 mb-8 text-white relative overflow-hidden transition-all duration-500 ${animateCards ? 'animate-slide-up' : 'opacity-0'}`}>
                        <div className="absolute inset-0 bg-gradient-to-r from-white/10 to-transparent"></div>
                        <div className="relative">
                            <div className="flex items-center justify-between mb-6">
                                <div>
                                    <h3 className="text-xl font-bold mb-2 flex items-center gap-2">
                                        <Star className="w-6 h-6 text-yellow-300" />
                                        {displayName}'s Position
                                    </h3>
                                    <div className="flex items-center gap-6">
                                        <div className="flex items-center gap-3">
                                            {getRankIcon(userPosition.rank)}
                                            <span className="text-3xl font-bold">#{userPosition.rank}</span>
                                            {getChangeIcon(userPosition.change)}
                                        </div>
                                        <div className="border-l border-white/30 pl-6">
                                            <p className="text-2xl font-bold">{userPosition.score} pts</p>
                                            <p className="text-sm opacity-90">{userPosition.examsCount} exams taken</p>
                                        </div>
                                        <div className="border-l border-white/30 pl-6">
                                            <p className="text-2xl font-bold">{userPosition.averageScore}%</p>
                                            <p className="text-sm opacity-90">Average score</p>
                                        </div>
                                    </div>
                                </div>
                                <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center backdrop-blur-sm">
                                    <User className="w-8 h-8" />
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Current Filter Info */}
                <div className="bg-gradient-to-r from-blue-100 to-indigo-100 border-2 border-blue-200 rounded-xl p-4 mb-8 transition-all duration-300 hover:shadow-lg">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3 text-blue-800">
                            <Filter className="w-5 h-5" />
                            <span className="font-semibold">
                                Showing {selectedSubject === 'global' ? 'Global' : selectedSubject} rankings for{' '}
                                {timePeriods.find(p => p.value === selectedTimePeriod)?.label}
                                {selectedGroup !== 'global' && (
                                    <span className="ml-2 px-2 py-1 bg-indigo-200 text-indigo-800 text-sm rounded-full">
                                        {userGroups.find(g => g.id.toString() === selectedGroup)?.name || 'Group'}
                                    </span>
                                )}
                            </span>
                        </div>
                        <ChevronRight className="w-5 h-5 text-blue-600" />
                    </div>
                </div>

                {/* Enhanced Top 3 Podium */}
                {leaderboardData.length >= 3 && !loading && (
                    <div className={`grid grid-cols-3 gap-6 mb-12 transition-all duration-700 ${animateCards ? 'animate-fade-in' : 'opacity-0'}`}>
                        {/* Second Place */}
                        <div className="bg-gradient-to-br from-gray-100 to-gray-200 rounded-2xl p-6 text-center transform translate-y-8 transition-all duration-500 hover:scale-105 shadow-lg">
                            <div className="w-20 h-20 bg-gradient-to-br from-gray-300 to-gray-400 rounded-full mx-auto mb-4 flex items-center justify-center shadow-lg">
                                <Medal className="w-10 h-10 text-gray-700" />
                            </div>
                            <h3 className="font-bold text-lg mb-2">{leaderboardData[1]?.name}</h3>
                            <div className="mb-2">{getUserGroupBadge(leaderboardData[1]?.groups)}</div>
                            <p className="text-3xl font-bold text-gray-700 mb-1">{leaderboardData[1]?.score}</p>
                            <p className="text-sm text-gray-600 mb-3">{leaderboardData[1]?.examsCount} exams</p>
                            <div className="px-4 py-2 bg-gradient-to-r from-gray-400 to-gray-500 text-white rounded-full text-sm font-semibold shadow-md">
                                🥈 2nd Place
                            </div>
                        </div>

                        {/* First Place */}
                        <div className="bg-gradient-to-br from-yellow-100 to-yellow-200 rounded-2xl p-8 text-center transition-all duration-500 hover:scale-105 shadow-2xl relative overflow-hidden">
                            <div className="absolute inset-0 bg-gradient-to-br from-yellow-200/50 to-transparent"></div>
                            <div className="relative">
                                <div className="w-24 h-24 bg-gradient-to-br from-yellow-400 to-yellow-500 rounded-full mx-auto mb-4 flex items-center justify-center shadow-xl animate-pulse">
                                    <Crown className="w-12 h-12 text-yellow-800" />
                                </div>
                                <h3 className="font-bold text-xl mb-2">{leaderboardData[0]?.name}</h3>
                                <div className="mb-2">{getUserGroupBadge(leaderboardData[0]?.groups)}</div>
                                <p className="text-4xl font-bold text-yellow-700 mb-1">{leaderboardData[0]?.score}</p>
                                <p className="text-sm text-gray-700 mb-4">{leaderboardData[0]?.examsCount} exams</p>
                                <div className="px-5 py-2 bg-gradient-to-r from-yellow-500 to-yellow-600 text-white rounded-full text-sm font-bold shadow-lg">
                                    👑 Champion
                                </div>
                            </div>
                        </div>

                        {/* Third Place */}
                        <div className="bg-gradient-to-br from-amber-100 to-amber-200 rounded-2xl p-6 text-center transform translate-y-8 transition-all duration-500 hover:scale-105 shadow-lg">
                            <div className="w-20 h-20 bg-gradient-to-br from-amber-400 to-amber-500 rounded-full mx-auto mb-4 flex items-center justify-center shadow-lg">
                                <Medal className="w-10 h-10 text-amber-800" />
                            </div>
                            <h3 className="font-bold text-lg mb-2">{leaderboardData[2]?.name}</h3>
                            <div className="mb-2">{getUserGroupBadge(leaderboardData[2]?.groups)}</div>
                            <p className="text-3xl font-bold text-amber-700 mb-1">{leaderboardData[2]?.score}</p>
                            <p className="text-sm text-gray-600 mb-3">{leaderboardData[2]?.examsCount} exams</p>
                            <div className="px-4 py-2 bg-gradient-to-r from-amber-500 to-amber-600 text-white rounded-full text-sm font-semibold shadow-md">
                                🥉 3rd Place
                            </div>
                        </div>
                    </div>
                )}

                {/* Enhanced Full Leaderboard Table */}
                <div className="bg-white/90 backdrop-blur-sm shadow-2xl rounded-2xl overflow-hidden border border-blue-100">
                    <div className="px-8 py-6 bg-gradient-to-r from-blue-600 to-indigo-600 text-white">
                        <h2 className="text-2xl font-bold flex items-center gap-3">
                            <TrendingUp className="w-6 h-6" />
                            Full Rankings
                        </h2>
                    </div>

                    {loading || examData.length === 0 ? (
                        <div className="p-12 text-center">
                            <div className="relative w-16 h-16 mx-auto mb-4">
                                <div className="absolute inset-0 border-4 border-blue-200 rounded-full"></div>
                                <div className="absolute inset-0 border-4 border-blue-600 rounded-full border-t-transparent animate-spin"></div>
                            </div>
                            <p className="text-lg text-gray-600 font-medium">
                                {examData.length === 0 ? 'No exam data available...' : 'Loading leaderboard...'}
                            </p>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead className="bg-gradient-to-r from-blue-50 to-indigo-50">
                                    <tr>
                                        <th className="px-8 py-4 text-left text-sm font-bold text-blue-800 uppercase tracking-wider">Rank</th>
                                        <th className="px-8 py-4 text-left text-sm font-bold text-blue-800 uppercase tracking-wider">Student</th>
                                        <th className="px-8 py-4 text-left text-sm font-bold text-blue-800 uppercase tracking-wider">Group</th>
                                        <th className="px-8 py-4 text-left text-sm font-bold text-blue-800 uppercase tracking-wider">Score</th>
                                        <th className="px-8 py-4 text-left text-sm font-bold text-blue-800 uppercase tracking-wider">Exams</th>
                                        <th className="px-8 py-4 text-left text-sm font-bold text-blue-800 uppercase tracking-wider">Average</th>
                                        <th className="px-8 py-4 text-left text-sm font-bold text-blue-800 uppercase tracking-wider">Questions</th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-blue-100">
                                    {leaderboardData.map((user, index) => (
                                        <tr
                                            key={user.user_id}
                                            style={{ animationDelay: `${index * 50}ms` }}
                                            className={`transition-all duration-300 animate-fade-in-up
        ${user.user_id === userData.user_id ? 'bg-gradient-to-r from-blue-100 to-indigo-100 ' : ''}
      `}
                                        >
                                            {/* Rank */}
                                            <td className="px-4 py-4 whitespace-nowrap text-center md:text-left">
                                                <div className="flex items-center justify-center md:justify-start gap-2">
                                                    {getRankIcon(user.rank)}
                                                    <span className={`px-2 py-1 rounded-full text-xs md:text-sm font-bold ${getRankBadgeColor(user.rank)}`}>
                                                        #{user.rank}
                                                    </span>
                                                </div>
                                            </td>

                                            {/* User */}
                                            <td className="px-4 py-4 whitespace-nowrap">
                                                <div className="flex items-center gap-3 flex-wrap md:flex-nowrap">
                                                    <div className="flex flex-col">
                                                        <span className="text-sm md:text-base font-semibold text-gray-900">
                                                            {user.name} {user.user_id === userData.user_id && <span className="ml-1 px-2 py-0.5 bg-blue-500 text-white text-xs rounded-full font-bold">Ward</span>}
                                                        </span>
                                                        <span className="text-gray-500 text-xs md:text-sm">{user.subject || 'All Subjects'}</span>
                                                    </div>
                                                </div>
                                            </td>

                                            {/* Group */}
                                            <td className="px-4 py-4 whitespace-nowrap">
                                                <div className="flex flex-wrap gap-1">
                                                    {getUserGroupBadge(user.groups)}
                                                </div>
                                            </td>

                                            {/* Score */}
                                            <td className="px-4 py-4 whitespace-nowrap text-center md:text-left">
                                                <span className="text-blue-600 font-bold text-sm md:text-lg">{user.score} pts</span>
                                            </td>

                                            {/* Exams Count */}
                                            <td className="px-4 py-4 whitespace-nowrap text-center md:text-left">
                                                <span className="text-gray-900 text-sm md:text-base">{user.examsCount}</span>
                                            </td>

                                            {/* Average Score */}
                                            <td className="px-4 py-4 whitespace-nowrap text-center md:text-left">
                                                <span className="text-gray-900 text-sm md:text-base">{user.averageScore}%</span>
                                            </td>

                                            {/* Correct/Total */}
                                            <td className="px-4 py-4 whitespace-nowrap text-center md:text-left">
                                                <span className="text-gray-900 text-sm md:text-base">{user.correctAnswers}/{user.totalQuestions}</span>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

                {/* Enhanced Stats Footer */}
                <div className="mt-12 grid grid-cols-1 md:grid-cols-4 gap-6">
                    {[
                        { title: "Total Participants", value: leaderboardData.length, icon: Users, gradient: "from-blue-500 to-blue-600" },
                        { title: `${displayName}'s Position`, value: `#${userPosition?.rank || 'N/A'}`, icon: TrendingUp, gradient: "from-indigo-500 to-indigo-600" },
                        {
                            title: "Subject Filter",
                            value: selectedSubject === 'global' ? 'Global' : selectedSubject,
                            icon: selectedSubject === 'global' ? Globe : () => <SubjectIcon subject={selectedSubject} />,
                            gradient: "from-purple-500 to-purple-600"
                        },
                        {
                            title: "Group Filter",
                            value: selectedGroup === 'global' ? 'All Groups' : userGroups.find(g => g.id.toString() === selectedGroup)?.name || 'Group',
                            icon: Shield,
                            gradient: "from-green-500 to-green-600"
                        }
                    ].map((stat, index) => (
                        <div
                            key={stat.title}
                            style={{ animationDelay: `${index * 100}ms` }}
                            className="bg-white/80 backdrop-blur-sm rounded-xl p-6 shadow-lg border border-blue-100 transition-all duration-300 hover:shadow-xl hover:scale-105 animate-fade-in-up group"
                        >
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm font-medium text-gray-600 mb-2">{stat.title}</p>
                                    <p className="text-2xl font-bold text-gray-900 truncate">{stat.value}</p>
                                </div>
                                <div className={`w-12 h-12 bg-gradient-to-r ${stat.gradient} rounded-lg flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                                    <stat.icon className="w-6 h-6 text-white" />
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            <style jsx>{`
                @keyframes fade-in {
                    from { opacity: 0; transform: translateY(20px); }
                    to { opacity: 1; transform: translateY(0); }
                }
                
                @keyframes fade-in-up {
                    from { opacity: 0; transform: translateY(30px); }
                    to { opacity: 1; transform: translateY(0); }
                }
                
                @keyframes slide-up {
                    from { opacity: 0; transform: translateY(50px); }
                    to { opacity: 1; transform: translateY(0); }
                }
                
                .animate-fade-in {
                    animation: fade-in 0.6s ease-out forwards;
                }
                
                .animate-fade-in-up {
                    animation: fade-in-up 0.6s ease-out forwards;
                }
                
                .animate-slide-up {
                    animation: slide-up 0.8s ease-out forwards;
                }
            `}</style>
        </div>
    );
}